import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DiamondVPNService from '../services/api';
import PaymentMethodModal from './PaymentMethodModal';
import { useTranslation } from '../hooks/useTranslation';

const Pricing = ({ isAuthenticated }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [totalGbInput, setTotalGbInput] = useState('0'); // total GB (base + extra)
  const [trafficPreset, setTrafficPreset] = useState(null); // total GB preset number|null
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('yookassa'); // 'yookassa' | 'platega' | 'tribute' | 'crypto'
  // Admin-controlled enabled payment methods. Defaults to all so behavior is
  // unchanged until the flags are loaded / if the request fails.
  const [enabledMethods, setEnabledMethods] = useState(['yookassa', 'tribute', 'platega', 'crypto']);
  const [isPaying, setIsPaying] = useState(false);
  const [isDraggingMonths, setIsDraggingMonths] = useState(false);
  const [isDraggingTraffic, setIsDraggingTraffic] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const monthsTrackRef = useRef(null);
  const trafficTrackRef = useRef(null);

  useEffect(() => {
    loadPricingPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    loadPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const res = await DiamondVPNService.getPaymentMethods();
      const map = res?.methods;
      if (map && typeof map === 'object') {
        const enabled = Object.keys(map).filter((k) => map[k]);
        if (enabled.length) setEnabledMethods(enabled);
      }
    } catch (e) {
      // keep all methods enabled (current behavior)
    }
  };

  const loadPricingPlans = async () => {
    try {
      const response = await DiamondVPNService.getSubscriptionPlans(i18n.language);
      const pricingData = response.plans;
      const sorted = Array.isArray(pricingData)
        ? [...pricingData].sort((a, b) => Number(a.price) - Number(b.price))
        : [];
      setPlans(sorted);
      const defaultPlan = pricingData.find((plan) => plan.is_popular) || sorted[0];
      setSelectedPlan(defaultPlan);
    } catch (error) {
      console.error('Error loading pricing plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthsFromDays = (days) => {
    const d = Number(days) || 0;
    if (d >= 330) return 12;
    if (d >= 150) return 6;
    if (d >= 75) return 3;
    return 1;
  };

  const toNumber = (v) => {
    const n = typeof v === 'string' ? Number(v.replace(',', '.')) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    const baseGb = toNumber(plan?.traffic_limit_gb);
    setTotalGbInput(String(baseGb));
    setTrafficPreset([500, 750, 1000].includes(baseGb) ? baseGb : null);
  };

  const handleTrafficPreset = (gb) => {
    const v = toNumber(gb);
    setTrafficPreset(v);
    setTotalGbInput(String(v));
  };

  const handleTotalGbChange = (value) => {
    setTrafficPreset(null);
    setTotalGbInput(value);
  };

  const normalizeTotalGb = () => {
    const baseGb = toNumber(selectedPlan?.traffic_limit_gb);

    if (totalGbInput === '') {
      setTotalGbInput(String(baseGb));
      return;
    }

    const v = toNumber(totalGbInput);
    const clamped = Math.max(baseGb, v);
    setTotalGbInput(String(clamped));
    setTrafficPreset([500, 750, 1000].includes(clamped) ? clamped : null);
  };

  const buildAllowedMethods = (extra, plategaAvail) => {
    const enabledSet = new Set(enabledMethods);
    const base = extra > 0
      ? (plategaAvail ? ['yookassa', 'platega', 'crypto'] : ['yookassa', 'crypto'])
      : (plategaAvail ? ['yookassa', 'platega', 'tribute', 'crypto'] : ['yookassa', 'tribute', 'crypto']);
    return base.filter((m) => enabledSet.has(m));
  };

  const buySelectedPlan = async () => {
    if (!selectedPlan) {
      alert(t('buttons.select_plan'));
      return;
    }
    if (!isAuthenticated) {
      navigate('/profile');
      return;
    }
    // Ensure the preselected method is actually offered (admin toggle aware).
    const baseGb = toNumber(selectedPlan?.traffic_limit_gb);
    const extra = Math.max(0, toNumber(totalGbInput) - baseGb);
    const allowed = buildAllowedMethods(extra, selectedPlan?.platega_available === true);
    if (allowed.length && !allowed.includes(paymentMethod)) {
      setPaymentMethod(allowed[0]);
    }
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    if (isPaying) return;
    setIsPaying(true);
    try {
      const baseGb = toNumber(selectedPlan?.traffic_limit_gb);
      const totalGb = Math.max(baseGb, toNumber(totalGbInput));
      const extraGbNum = Math.max(0, totalGb - baseGb);
      if (paymentMethod === 'yookassa') {
        const res = await DiamondVPNService.createPayment(selectedPlan.id, extraGbNum);
        if (res?.success && res?.confirmation_url) {
          window.location.href = res.confirmation_url;
        } else {
          throw new Error(t('pricing.builder.errorYooKassa'));
        }
      } else if (paymentMethod === 'platega') {
        const res = await DiamondVPNService.createPlategaPayment(selectedPlan.id, extraGbNum);
        if (res?.success && res?.redirect_url) {
          window.location.href = res.redirect_url;
        } else {
          throw new Error(t('pricing.builder.errorPlatega'));
        }
      } else if (paymentMethod === 'tribute') {
        if (extraGbNum > 0) {
          alert(t('pricing.builder.tributeExtraUnavailable'));
          return;
        }
        const res = await DiamondVPNService.createTributePayment(selectedPlan.id);
        if (res?.success && res?.redirect_url) {
          window.location.href = res.redirect_url;
        } else {
          throw new Error(t('pricing.builder.errorTribute'));
        }
      } else if (paymentMethod === 'crypto') {
        const res = await DiamondVPNService.createCryptoPayment(selectedPlan.id, extraGbNum);
        if (res?.success && res?.redirect_url) {
          window.location.href = res.redirect_url;
        } else {
          throw new Error(t('pricing.builder.errorCrypto'));
        }
      }
    } catch (error) {
      console.error('Error preparing payment:', error);
      const msg = t('paymentModal.error.generic') || error.message || 'Payment error';
      alert(msg);
    } finally {
      setIsPaying(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('pricing.loading')}</div>;
  }

  const currency = selectedPlan?.currency || '';
  const basePrice = toNumber(selectedPlan?.price);
  const pricePerGbPerMonth = toNumber(selectedPlan?.price_per_gb_per_month);
  const months = monthsFromDays(selectedPlan?.days);
  const baseGb = toNumber(selectedPlan?.traffic_limit_gb);
  const totalGb = Math.max(baseGb, toNumber(totalGbInput));
  const extraGb = Math.max(0, totalGb - baseGb);
  const extraCost = extraGb * pricePerGbPerMonth * months;
  const totalPrice = Math.round((basePrice + extraCost) * 100) / 100;

  const isPlategaAvailable = selectedPlan?.platega_available === true;
  const allowedMethods = buildAllowedMethods(extraGb, isPlategaAvailable);

  const monthOptionMap = new Map();
  for (const p of plans) {
    const m = monthsFromDays(p.days);
    if (!monthOptionMap.has(m)) monthOptionMap.set(m, p);
  }

  const monthOptions = Array.from(monthOptionMap.entries())
    .map(([monthsValue, plan]) => ({ plan, months: monthsValue }))
    .sort((a, b) => a.months - b.months);

  const selectedMonths = selectedPlan ? monthsFromDays(selectedPlan.days) : null;
  const monthValues = monthOptions.map((x) => x.months);
  const selectedMonthIndex = Math.max(0, monthValues.findIndex((m) => m === selectedMonths));
  const monthsPos = monthValues.length > 1 ? (selectedMonthIndex / (monthValues.length - 1)) : 0;

  const trafficValues = [500, 750, 1000];
  const trafficIndex = trafficPreset != null ? trafficValues.indexOf(trafficPreset) : -1;
  let trafficPos = 0;
  if (trafficIndex >= 0 && trafficValues.length > 1) {
    trafficPos = trafficIndex / (trafficValues.length - 1);
  } else if (trafficValues.length > 1) {
    const minV = trafficValues[0];
    const maxV = trafficValues[trafficValues.length - 1];
    const v = totalGb;
    const raw = (v - minV) / (maxV - minV);
    trafficPos = Math.max(0, Math.min(1, raw));
  }

  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  const setMonthsFromPos = (pos) => {
    const count = monthOptions.length;
    if (count <= 0) return;
    if (count === 1) {
      handlePlanSelect(monthOptions[0].plan);
      return;
    }
    const idx = Math.round(clamp01(pos) * (count - 1));
    const next = monthOptions[idx];
    if (next?.plan) handlePlanSelect(next.plan);
  };

  const updateMonthsFromClientX = (clientX) => {
    const el = monthsTrackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cs = window.getComputedStyle(el);
    const pad = parseFloat(cs.getPropertyValue('--pad')) || 42;
    const cols = parseFloat(cs.getPropertyValue('--cols')) || monthOptions.length || 4;

    const content = rect.width - (2 * pad);
    const halfStep = cols > 0 ? (content / (2 * cols)) : 0;

    const left = rect.left + pad + halfStep;
    const right = rect.right - pad - halfStep;
    if (right <= left) return;

    const x = Math.max(left, Math.min(right, clientX));
    const pos = (x - left) / (right - left);
    setMonthsFromPos(pos);
  };

  const startMonthsDrag = (e) => {
    e.preventDefault();
    setIsDraggingMonths(true);
    updateMonthsFromClientX(e.clientX);

    const onMove = (ev) => updateMonthsFromClientX(ev.clientX);
    const onUp = () => {
      setIsDraggingMonths(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const setTrafficFromPos = (pos) => {
    if (!trafficValues.length) return;
    const minV = trafficValues[0];
    const maxV = trafficValues[trafficValues.length - 1];
    const raw = minV + clamp01(pos) * (maxV - minV);
    const rounded = Math.round(raw);

    const snapTolerance = 10;
    const snap = trafficValues.find((v) => Math.abs(v - rounded) <= snapTolerance);
    if (snap != null) {
      handleTrafficPreset(snap);
      return;
    }
    setTrafficPreset(null);
    setTotalGbInput(String(Math.max(baseGb, rounded)));
  };

  const updateTrafficFromClientX = (clientX) => {
    const el = trafficTrackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cs = window.getComputedStyle(el);
    const pad = parseFloat(cs.getPropertyValue('--pad')) || 42;
    const cols = parseFloat(cs.getPropertyValue('--cols')) || trafficValues.length || 3;

    const content = rect.width - (2 * pad);
    const halfStep = cols > 0 ? (content / (2 * cols)) : 0;

    const left = rect.left + pad + halfStep;
    const right = rect.right - pad - halfStep;
    if (right <= left) return;

    const x = Math.max(left, Math.min(right, clientX));
    const pos = (x - left) / (right - left);
    setTrafficFromPos(pos);
  };

  const startTrafficDrag = (e) => {
    e.preventDefault();
    setIsDraggingTraffic(true);
    updateTrafficFromClientX(e.clientX);

    const onMove = (ev) => updateTrafficFromClientX(ev.clientX);
    const onUp = () => {
      setIsDraggingTraffic(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div className="tariff-card">
      <div className="tariff-card__title">{t('pricing.builder.title')}</div>

      <div className="tariff-card__inner">
        <div className="tariff-line" style={{ ['--cols']: monthOptions.length }}>
          <div className="tariff-line__label">{t('pricing.builder.months')}</div>
          <div className="tariff-line__track" ref={monthsTrackRef} onPointerDown={startMonthsDrag}>
            <div
              className={`tariff-line__dot tariff-line__dot--jump ${isDraggingMonths ? 'is-dragging' : ''}`}
              style={{ ['--pos']: monthsPos }}
              role="slider"
              tabIndex={0}
              aria-label={t('pricing.builder.months')}
              aria-valuemin={monthOptions[0]?.months || 1}
              aria-valuemax={monthOptions[monthOptions.length - 1]?.months || 12}
              aria-valuenow={selectedMonths || 1}
              onPointerDown={(e) => {
                e.stopPropagation();
                startMonthsDrag(e);
              }}
            >
              <div className="tariff-line__dot-inner" />
            </div>
            <div className="tariff-line__bar" aria-hidden="true" />
          </div>
          <div className="tariff-line__values">
            {monthOptions.map(({ plan, months: m }) => (
              <button
                key={plan.id}
                type="button"
                className={`tariff-line__value ${selectedMonths === m ? 'is-active' : ''}`}
                onClick={() => handlePlanSelect(plan)}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div className="tariff-line" style={{ ['--cols']: trafficValues.length }}>
          <div className="tariff-line__label">{t('pricing.builder.traffic')}</div>
          <div className="tariff-line__track" ref={trafficTrackRef} onPointerDown={startTrafficDrag}>
            <div
              className={`tariff-line__dot tariff-line__dot--smooth ${isDraggingTraffic ? 'is-dragging' : ''}`}
              style={{ ['--pos']: trafficPos }}
              role="slider"
              tabIndex={0}
              aria-label={t('pricing.builder.traffic')}
              aria-valuemin={trafficValues[0]}
              aria-valuemax={trafficValues[trafficValues.length - 1]}
              aria-valuenow={totalGb}
              onPointerDown={(e) => {
                e.stopPropagation();
                startTrafficDrag(e);
              }}
            >
              <div className="tariff-line__dot-inner" />
            </div>
            <div className="tariff-line__bar" aria-hidden="true" />
          </div>
          <div className="tariff-line__values">
            {trafficValues.map((gb) => (
              <button
                key={gb}
                type="button"
                className={`tariff-line__value ${trafficPreset === gb ? 'is-active' : ''}`}
                onClick={() => handleTrafficPreset(gb)}
              >
                {gb}
              </button>
            ))}
          </div>
        </div>

        <div className="tariff-custom">
          <div className="tariff-custom__label">{t('pricing.builder.ownGb')}</div>
          <div className="tariff-custom__field">
            {totalGbInput === '' ? (
              <div className="tariff-custom__placeholder">
                {t('pricing.builder.ownGbPlaceholder', { min: baseGb })}
              </div>
            ) : null}
            <input
              className="tariff-custom__input"
              type="number"
              min={baseGb}
              step="1"
              value={totalGbInput}
              onChange={(e) => handleTotalGbChange(e.target.value)}
              onBlur={normalizeTotalGb}
              aria-label={t('pricing.builder.ownGb')}
            />
          </div>
        </div>
      </div>

      <div className="tariff-card__payWrap">
        <button
          className="btn btn-primary tariff-card__payBtn"
          onClick={buySelectedPlan}
          disabled={!selectedPlan}
        >
          {t('pricing.builder.payAmount', { amount: totalPrice, currency })}
        </button>
      </div>

      <PaymentMethodModal
        open={showPaymentModal}
        method={paymentMethod}
        onChangeMethod={setPaymentMethod}
        onConfirm={confirmPayment}
        onCancel={() => setShowPaymentModal(false)}
        isLoading={isPaying}
        allowedMethods={allowedMethods}
        enabledMethods={enabledMethods}
      />
    </div>
  );
};

export default Pricing;
